import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usuariosApi } from '../../api/endpoints'
import { LoadingState, ErrorState, EmptyState } from '../../components/ui/EmptyState'
import { Button, IconButton } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Input, PasswordInput, Select } from '../../components/ui/Input'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { formatDate } from '../../lib/utils'
import { STATUS_ASSINATURA_LABEL, type StatusAssinatura } from '../../lib/enums'
import type { FiltroUsuarios, UsuarioItem, UserRole } from '../../types'

const ROLES: UserRole[] = ['ADMIN', 'PRO', 'PREMIUM', 'BASIC']

const ROLE_VARIANT: Record<UserRole, 'expense' | 'warning' | 'primary' | 'muted'> = {
  ADMIN: 'expense',
  PRO: 'warning',
  PREMIUM: 'primary',
  BASIC: 'muted',
}

function primaryRole(u: UsuarioItem): UserRole {
  const role = u.roles[0]
  return ROLES.includes(role as UserRole) ? (role as UserRole) : 'BASIC'
}

const PLANOS = ['BASIC', 'PREMIUM', 'PRO']
const ORDENACOES: { valor: NonNullable<FiltroUsuarios['ordenar']>; rotulo: string }[] = [
  { valor: 'nome', rotulo: 'Nome' }, { valor: 'cadastro', rotulo: 'Data de cadastro' }, { valor: 'plano', rotulo: 'Plano' },
  { valor: 'assinatura', rotulo: 'Situação da assinatura' }, { valor: 'renovacao', rotulo: 'Próxima renovação' },
]
const ASSINATURA_VARIANT: Record<number, 'income' | 'info' | 'warning' | 'expense' | 'muted'> = { 1: 'info', 2: 'income', 3: 'warning', 4: 'expense', 5: 'muted' }

export function UsuariosPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const [modal, setModal] = useState<'novo' | 'nivel' | null>(null)
  const [selecionado, setSelecionado] = useState<UsuarioItem | null>(null)
  const [paraExcluir, setParaExcluir] = useState<UsuarioItem | null>(null)
  const [form, setForm] = useState({ nome: '', email: '', senha: '', role: 'BASIC' as UserRole })
  const [erro, setErro] = useState<string | null>(null)

  const [busca, setBusca] = useState('')
  const [termo, setTermo] = useState('')
  const [nivel, setNivel] = useState('')
  const [plano, setPlano] = useState('')
  const [assinatura, setAssinatura] = useState('')
  const [ordenar, setOrdenar] = useState<NonNullable<FiltroUsuarios['ordenar']>>('nome')
  const [desc, setDesc] = useState(false)
  const [pagina, setPagina] = useState(1)

  useEffect(() => {
    const t = setTimeout(() => { setTermo(busca.trim()); setPagina(1) }, 350)
    return () => clearTimeout(t)
  }, [busca])

  const filtro: FiltroUsuarios = {
    pagina, busca: termo || undefined, nivel: nivel || undefined, plano: plano || undefined,
    statusAssinatura: assinatura === '' ? undefined : Number(assinatura), ordenar, desc,
  }
  const temFiltro = !!(termo || nivel || plano || assinatura !== '')

  const { data: resultado, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['usuarios', filtro],
    queryFn: () => usuariosApi.listar(filtro),
    placeholderData: (anterior) => anterior,
  })
  const usuarios = resultado?.itens ?? []
  const totalPaginas = Math.max(1, Math.ceil((resultado?.totalRegistros ?? 0) / (resultado?.itensPorPagina ?? 20)))

  function limparFiltros() {
    setBusca(''); setTermo(''); setNivel(''); setPlano(''); setAssinatura(''); setOrdenar('nome'); setDesc(false); setPagina(1)
  }
  const mudar = (f: () => void) => { f(); setPagina(1) }

  const criar = useMutation({
    mutationFn: () => usuariosApi.criar({ name: form.nome, email: form.email, password: form.senha, role: form.role }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usuarios'] })
      toast.success('Usuário criado.')
      setModal(null)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const alterarRole = useMutation({
    mutationFn: () => usuariosApi.alterarRole(selecionado!.id, form.role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usuarios'] })
      toast.success('Nível de acesso alterado.')
      setModal(null)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const remover = useMutation({
    mutationFn: (id: string) => usuariosApi.remover(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usuarios'] })
      toast.success('Usuário excluído.')
      setParaExcluir(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  function openNovo() {
    setSelecionado(null)
    setForm({ nome: '', email: '', senha: '', role: 'BASIC' })
    setErro(null)
    setModal('novo')
  }

  function openNivel(u: UsuarioItem) {
    setSelecionado(u)
    setForm({ nome: u.name, email: u.email, senha: '', role: primaryRole(u) })
    setErro(null)
    setModal('nivel')
  }

  function submit() {
    if (modal === 'nivel') {
      alterarRole.mutate()
      return
    }
    if (!form.nome.trim() || !form.email.trim() || !form.senha) {
      setErro('Preencha nome, e-mail e senha.')
      return
    }
    criar.mutate()
  }

  return (
    <>
      <PageHeader title="Usuários" />

      <Card>
        <CardHeader
          title={<>Usuários <Badge variant="muted" className="ml-1">{resultado?.totalRegistros ?? 0}</Badge></>}
          actions={
            <>
              <Button variant="soft-info" icon="ri-refresh-line" onClick={() => refetch()} loading={isFetching}>Atualizar</Button>
              <Button icon="ri-user-add-line" onClick={openNovo}>Novo usuário</Button>
            </>
          }
        />
        <div className="grid gap-3 border-b border-border/60 p-4 sm:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <Input label="Buscar" placeholder="Nome ou e-mail" value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <Select label="Nível" value={nivel} onChange={(e) => mudar(() => setNivel(e.target.value))}>
            <option value="">Todos</option>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
          <Select label="Plano" value={plano} onChange={(e) => mudar(() => setPlano(e.target.value))}>
            <option value="">Todos</option>
            {PLANOS.map((p) => <option key={p} value={p}>{p}</option>)}
          </Select>
          <Select label="Assinatura" value={assinatura} onChange={(e) => mudar(() => setAssinatura(e.target.value))}>
            <option value="">Todas</option>
            <option value="0">Nunca assinou</option>
            {Object.entries(STATUS_ASSINATURA_LABEL).map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
          </Select>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Select label="Ordenar por" value={ordenar} onChange={(e) => mudar(() => setOrdenar(e.target.value as NonNullable<FiltroUsuarios['ordenar']>))}>
                {ORDENACOES.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
              </Select>
            </div>
            <IconButton icon={desc ? 'ri-sort-desc' : 'ri-sort-asc'} label={desc ? 'Ordem decrescente' : 'Ordem crescente'} tone="info" onClick={() => mudar(() => setDesc(!desc))} />
          </div>
          {temFiltro && (
            <div className="sm:col-span-2 lg:col-span-6">
              <Button size="sm" variant="light" icon="ri-close-line" onClick={limparFiltros}>Limpar filtros</Button>
            </div>
          )}
        </div>
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message="Erro ao carregar usuários." />
        ) : usuarios.length === 0 ? (
          <EmptyState icon="ri-team-line" message={temFiltro ? "Nenhum usuário com esses filtros." : "Nenhum usuário cadastrado."} />
        ) : (
          <Table>
            <THead>
              <tr><Th>Nome</Th><Th>E-mail</Th><Th>Nível</Th><Th>Plano</Th><Th>Assinatura</Th><Th>Criado em</Th><Th className="text-right">Ações</Th></tr>
            </THead>
            <tbody>
              {usuarios.map((u) => {
                const role = primaryRole(u)
                return (
                  <Tr key={u.id}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                          {u.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
                        </span>
                        <span className="font-medium text-white">{u.name}</span>
                      </div>
                    </Td>
                    <Td className="text-muted">{u.email}</Td>
                    <Td><Badge variant={ROLE_VARIANT[role]}>{role}</Badge></Td>
                    <Td className="text-muted">{u.plano ?? '—'}</Td>
                    <Td>
                      {u.statusAssinatura ? (
                        <div className="space-y-0.5">
                          <Badge variant={ASSINATURA_VARIANT[u.statusAssinatura] ?? 'muted'}>{STATUS_ASSINATURA_LABEL[u.statusAssinatura as StatusAssinatura] ?? '—'}</Badge>
                          {(u.statusAssinatura === 1 ? u.trialFimEm : u.renovaEm) && (
                            <p className="text-2xs text-muted">{u.statusAssinatura === 1 ? 'Teste até' : 'Renova em'} {formatDate((u.statusAssinatura === 1 ? u.trialFimEm : u.renovaEm)!)}</p>
                          )}
                        </div>
                      ) : <span className="text-13 text-muted">Nunca assinou</span>}
                    </Td>
                    <Td className="whitespace-nowrap text-muted">{formatDate(u.createdAt)}</Td>
                    <Td className="whitespace-nowrap text-right">
                      <IconButton icon="ri-shield-user-line" label={`Alterar nível de ${u.name}`} tone="info" onClick={() => openNivel(u)} />
                      <IconButton icon="ri-delete-bin-5-fill" label={`Excluir ${u.name}`} tone="danger" onClick={() => setParaExcluir(u)} />
                    </Td>
                  </Tr>
                )
              })}
            </tbody>
          </Table>
        )}
        {totalPaginas > 1 && (
          <div className="flex items-center justify-between border-t border-border/60 p-3 text-13 text-muted">
            <span>Página {pagina} de {totalPaginas}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="light" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>Anterior</Button>
              <Button size="sm" variant="light" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)}>Próxima</Button>
            </div>
          </div>
        )}
      </Card>

      <Modal
        open={modal !== null}
        onClose={() => setModal(null)}
        title={modal === 'novo' ? 'Novo usuário' : `Nível de acesso · ${selecionado?.name ?? ''}`}
        size={modal === 'novo' ? 'md' : 'sm'}
        footer={
          <>
            <Button variant="light" onClick={() => setModal(null)}>Cancelar</Button>
            <Button icon="ri-save-line" onClick={submit} loading={criar.isPending || alterarRole.isPending}>
              {modal === 'novo' ? 'Criar usuário' : 'Salvar nível'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {modal === 'novo' && (
            <>
              <Input label="Nome" value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} autoFocus />
              <Input label="E-mail" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} autoComplete="off" />
              <PasswordInput label="Senha" value={form.senha} onChange={(e) => setForm((f) => ({ ...f, senha: e.target.value }))} autoComplete="new-password" />
            </>
          )}
          <Select label="Nível de acesso" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as UserRole }))}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!paraExcluir}
        onClose={() => setParaExcluir(null)}
        onConfirm={() => paraExcluir && remover.mutate(paraExcluir.id)}
        loading={remover.isPending}
        message={<>O usuário <strong className="text-white">{paraExcluir?.name}</strong> perderá o acesso ao Walleon.</>}
      />
    </>
  )
}
